import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Container, Header, Segment, Button, Icon, Item, Message, Loader, Divider } from 'semantic-ui-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { incrementQuantity, decrementQuantity, removeItem } from '../../reducers/cartSlice';
import ProfileNav from '../CaptainProfileEdit/ProfileNav/ProfileNav';
import Footer from '../Footer/Footer';
import './CartPage.css';

const CartPage = () => {
  const dispatch = useDispatch();
  const { items: cartItems } = useSelector((state) => state.cart);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const handleIncrement = (cartItemId) => {
    dispatch(incrementQuantity(cartItemId));
  };

  const handleDecrement = (cartItemId) => {
    dispatch(decrementQuantity(cartItemId));
  };

  const handleRemove = (cartItemId) => {
    dispatch(removeItem(cartItemId));
  };

  useEffect(() => {
    if (cartItems.length === 0) {
      setQuote(null);
      return;
    }

    const fetchQuote = async () => {
      setQuoteLoading(true);
      try {
        const functions = getFunctions();
        const getProductCartQuote = httpsCallable(functions, 'getProductCartQuote');
        const itemsForBackend = cartItems.map(item => ({
            productId: item.id,
            quantity: item.quantity,
        }));
        const result = await getProductCartQuote({ cartItems: itemsForBackend });
        if (result.data.success) {
          setQuote(result.data.quote);
        }
      } catch (error) {
        console.error("Error fetching cart quote:", error);
      } finally {
        setQuoteLoading(false);
      }
    };

    fetchQuote();
  }, [cartItems]);

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <>
      <ProfileNav />
      <div className="cart-page-container">
        <Container>
          <Header as="h1" className="cart-header">Your Shopping Cart</Header>
          <Link to="/products"> Continue shopping BoatRBO Marketplace</Link>        
          {cartItems.length === 0 ? (
            <Message info>
              <Message.Header>Your cart is empty.</Message.Header>
              <p>
                Looks like you haven't added any products yet. Check out our <Link to="/products">marketplace</Link>!
              </p>
            </Message>
          ) : (
            <>
              <Segment padded>
                <Item.Group divided>
                  {cartItems.map((item) => (
                    <Item key={item.cartItemId} className="cart-item">
                      <Item.Image src={item.imageUrls?.[0] || 'https://react.semantic-ui.com/images/wireframe/image.png'} size="small" />
                      <Item.Content>
                        <Item.Header as={Link} to={`/products`}>{item.name}</Item.Header>
                        <Item.Meta>
                          <span className="price">${Number(item.price).toFixed(2)}</span>
                        </Item.Meta>
                        {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                          <Item.Description>
                            {Object.entries(item.selectedOptions).map(([key, value]) => (
                              <p key={key}><strong>{key}:</strong> {value}</p>
                            ))}
                          </Item.Description>
                        )}
                        <Item.Extra>
                          <div className="quantity-controls">
                            <Button icon size="mini" onClick={() => handleDecrement(item.cartItemId)}>
                              <Icon name="minus" />
                            </Button>
                            <span className="quantity-display">{item.quantity}</span>
                            <Button icon size="mini" onClick={() => handleIncrement(item.cartItemId)}>
                              <Icon name="plus" />
                            </Button>
                          </div>
                          <Button basic color="red" floated="right" onClick={() => handleRemove(item.cartItemId)}>
                            Remove
                          </Button>
                        </Item.Extra>
                      </Item.Content>
                    </Item>
                  ))}
                </Item.Group>
              </Segment>
              <Segment className="cart-summary">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'left', minWidth: '250px', flexGrow: 1, marginRight: '20px' }}>
                    {quoteLoading ? (
                      <Loader active inline="centered" size="small" />
                    ) : quote ? (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span>Subtotal:</span>
                          <span>${quote.subtotal.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span>Shipping:</span>
                          <span>${quote.shipping.toFixed(2)}</span>
                        </div>
                        {quote.handling > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span>Handling:</span>
                            <span>${quote.handling.toFixed(2)}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span>Tax:</span>
                          <span>${quote.tax.toFixed(2)}</span>
                        </div>
                        <Divider />
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                          <Header as="h3" style={{ margin: 0 }}>Total:</Header>
                          <Header as="h3" style={{ margin: 0 }}>${quote.total.toFixed(2)}</Header>
                        </div>
                      </>
                    ) : (
                      <Header as="h3">
                        Subtotal: ${subtotal.toFixed(2)}
                      </Header>
                    )}
                  </div>
                  <Button as={Link} to="/product-checkout" color="green" size="large" style={{ marginTop: '10px' }}>
                    Proceed to Checkout
                  </Button>
                </div>
              </Segment>
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  );
};

export default CartPage;
